import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TechNode {
  id: string;
  nom: string;
  description: string;
  categorie: 'personnel' | 'vehicules' | 'operations' | 'infrastructure';
  position_x: number;
  position_y: number;
  cout_argent: number;
  cout_medailles: number;
  duree_recherche_secondes: number;
  prerequis: string[];
  effets: string[];
  icone: string;
}

interface PlayerNodeState {
  node_id: string;
  statut: 'en_recherche' | 'complete';
  started_at: string;
  completed_at: string | null;
  remaining_seconds: number | null;
}

interface TechTreeState {
  nodes: TechNode[];
  player: PlayerNodeState[];
}

// ─── Constantes visuelles ─────────────────────────────────────────────────────

const CATEGORIES = ['personnel', 'vehicules', 'operations', 'infrastructure'] as const;

const CAT_CONFIG: Record<string, { label: string; color: string; border: string; bg: string; dot: string }> = {
  personnel:      { label: 'Personnel',      color: 'text-sky-300',     border: 'border-sky-500/40',     bg: 'bg-sky-500/10',     dot: 'bg-sky-400' },
  vehicules:      { label: 'Véhicules',      color: 'text-amber-300',   border: 'border-amber-500/40',   bg: 'bg-amber-500/10',   dot: 'bg-amber-400' },
  operations:     { label: 'Opérations',     color: 'text-emerald-300', border: 'border-emerald-500/40', bg: 'bg-emerald-500/10', dot: 'bg-emerald-400' },
  infrastructure: { label: 'Infrastructure', color: 'text-purple-300',  border: 'border-purple-500/40',  bg: 'bg-purple-500/10',  dot: 'bg-purple-400' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(secs: number): string {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}min`;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}

function formatCountdown(secs: number): string {
  if (secs <= 0) return 'Complété !';
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m < 60) return `${m}:${String(s).padStart(2, '0')}`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${String(rm).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// ─── Composant principal ──────────────────────────────────────────────────────

const TechTreeView: React.FC = () => {
  const [state, setState] = useState<TechTreeState | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<TechNode | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('personnel');
  const [tick, setTick] = useState(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const getAuthHeaders = (): HeadersInit => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchState = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/tech-tree`, {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) setState(data.data);
    } catch {
      // noop
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchState();
    // Countdown tick toutes les secondes
    tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, [fetchState]);

  // Rafraîchir l'état toutes les 16s (légèrement plus que le ticker serveur à 15s)
  useEffect(() => {
    const interval = setInterval(fetchState, 16_000);
    return () => clearInterval(interval);
  }, [fetchState]);

  const getPlayerNode = (nodeId: string): PlayerNodeState | null =>
    state?.player.find((p) => p.node_id === nodeId) ?? null;

  const isComplete = (nodeId: string) => getPlayerNode(nodeId)?.statut === 'complete';

  const isResearching = (nodeId: string) => getPlayerNode(nodeId)?.statut === 'en_recherche';

  const isUnlocked = (node: TechNode): boolean => {
    if (!state) return false;
    return node.prerequis.every((pId) => isComplete(pId));
  };

  const anyResearchInProgress = (): boolean =>
    state?.player.some((p) => p.statut === 'en_recherche') ?? false;

  const getRemainingSeconds = (nodeId: string): number => {
    if (!state) return 0;
    const p = getPlayerNode(nodeId);
    if (!p || p.statut !== 'en_recherche') return 0;
    const node = state.nodes.find((n) => n.id === nodeId);
    if (!node) return 0;
    const elapsed = (Date.now() - new Date(p.started_at).getTime()) / 1000;
    return Math.max(0, Math.ceil(node.duree_recherche_secondes - elapsed));
  };

  const getProgress = (nodeId: string): number => {
    if (!state) return 0;
    const p = getPlayerNode(nodeId);
    if (!p || p.statut !== 'en_recherche') return 0;
    const node = state.nodes.find((n) => n.id === nodeId);
    if (!node) return 0;
    const elapsed = (Date.now() - new Date(p.started_at).getTime()) / 1000;
    return Math.min(100, (elapsed / node.duree_recherche_secondes) * 100);
  };

  const handleStart = async (nodeId: string) => {
    setActionLoading(true);
    setActionError(null);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/tech-tree/${nodeId}/start`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!data.success) { setActionError(data.error); return; }
      setState(data.data);
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (nodeId: string) => {
    setActionLoading(true);
    setActionError(null);
    try {
      const res = await fetch(`${getApiBaseUrl(true)}/tech-tree/${nodeId}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (!data.success) { setActionError(data.error); return; }
      setState(data.data);
    } catch (e: any) {
      setActionError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
        Chargement de l'arbre de technologies…
      </div>
    );
  }

  if (!state) {
    return (
      <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
        Impossible de charger l'arbre de technologies.
      </div>
    );
  }

  const catNodes = state.nodes.filter((n) => n.categorie === activeCategory);
  // Organiser par colonnes
  const maxCol = Math.max(...catNodes.map((n) => n.position_x), 0);
  const columns: TechNode[][] = Array.from({ length: maxCol + 1 }, (_, col) =>
    catNodes.filter((n) => n.position_x === col).sort((a, b) => a.position_y - b.position_y)
  );

  // Nœud en recherche active (toutes catégories)
  const activeResearch = state.player.find((p) => p.statut === 'en_recherche');
  const activeResearchNode = activeResearch
    ? state.nodes.find((n) => n.id === activeResearch.node_id)
    : null;

  return (
    <div className="h-full flex flex-col bg-zinc-950/50">

      {/* Header */}
      <div className="shrink-0 px-5 pt-4 pb-3 border-b border-zinc-800/60">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-bold text-white">Arbre de Technologies</h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              {state.player.filter((p) => p.statut === 'complete').length} / {state.nodes.length} technologies débloquées
            </p>
          </div>
          {activeResearchNode && (
            <div className="flex items-center gap-2.5 bg-zinc-900 border border-zinc-700/60 rounded-xl px-3 py-2">
              <span className="text-lg">{activeResearchNode.icone}</span>
              <div>
                <p className="text-[11px] text-zinc-400">Recherche en cours</p>
                <p className="text-xs font-semibold text-white truncate max-w-[140px]">{activeResearchNode.nom}</p>
                <p className="text-[11px] text-sky-400 font-mono">
                  {formatCountdown(getRemainingSeconds(activeResearchNode.id))}
                </p>
              </div>
              {/* Mini barre de progression */}
              <div className="w-20 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-sky-500 transition-all duration-1000"
                  style={{ width: `${getProgress(activeResearchNode.id)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Onglets catégories */}
        <div className="flex gap-1">
          {CATEGORIES.map((cat) => {
            const cfg = CAT_CONFIG[cat];
            const completedCount = state.nodes
              .filter((n) => n.categorie === cat)
              .filter((n) => isComplete(n.id)).length;
            const totalCount = state.nodes.filter((n) => n.categorie === cat).length;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => { setActiveCategory(cat); setSelectedNode(null); setActionError(null); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                  activeCategory === cat
                    ? `${cfg.bg} ${cfg.color} ${cfg.border}`
                    : 'bg-zinc-800/40 text-zinc-500 border-zinc-700/30 hover:text-zinc-300'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                {cfg.label}
                <span className="text-[10px] opacity-60">{completedCount}/{totalCount}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Contenu — Arbre + Panneau détail */}
      <div className="flex-1 min-h-0 flex overflow-hidden">

        {/* Arbre visuel */}
        <div className="flex-1 overflow-auto p-5">
          <div className="flex items-start gap-0 min-w-max">
            {columns.map((col, colIdx) => (
              <React.Fragment key={colIdx}>
                {/* Colonne de nœuds */}
                <div className="flex flex-col gap-4">
                  {col.map((node) => {
                    const complete = isComplete(node.id);
                    const researching = isResearching(node.id);
                    const unlocked = isUnlocked(node);
                    const locked = !unlocked && !complete && !researching;
                    const cfg = CAT_CONFIG[node.categorie];

                    let ringClass = 'border-zinc-700/50 bg-zinc-900/60';
                    if (complete)    ringClass = `border-emerald-500/60 bg-emerald-950/40`;
                    else if (researching) ringClass = `border-sky-500/60 bg-sky-950/30`;
                    else if (unlocked)    ringClass = `${cfg.border} ${cfg.bg}`;

                    const isSelected = selectedNode?.id === node.id;

                    return (
                      <button
                        key={node.id}
                        type="button"
                        onClick={() => { setSelectedNode(node); setActionError(null); }}
                        className={`
                          relative w-52 text-left rounded-xl border p-3.5 transition-all duration-150
                          ${ringClass}
                          ${isSelected ? 'ring-2 ring-sky-400/50 shadow-lg shadow-sky-900/20' : 'hover:brightness-110'}
                          ${locked ? 'opacity-40 grayscale' : ''}
                        `}
                      >
                        {/* Icône + nom */}
                        <div className="flex items-start gap-2.5 mb-2">
                          <span className="text-xl shrink-0 leading-none mt-0.5">{node.icone}</span>
                          <div className="min-w-0">
                            <p className={`text-xs font-bold leading-tight ${complete ? 'text-emerald-300' : researching ? 'text-sky-300' : unlocked ? cfg.color : 'text-zinc-500'}`}>
                              {node.nom}
                            </p>
                            {!complete && !researching && (
                              <p className="text-[10px] text-zinc-600 mt-0.5">
                                {node.cout_argent > 0 ? `${node.cout_argent.toLocaleString('fr-FR')} €` : 'Gratuit'}
                                {node.cout_medailles > 0 ? ` · ${node.cout_medailles} 🏅` : ''}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Statut */}
                        {complete && (
                          <div className="flex items-center gap-1 text-[10px] text-emerald-400 font-semibold">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Débloquée
                          </div>
                        )}
                        {researching && (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] text-sky-400 font-semibold">En recherche…</span>
                              <span className="text-[10px] text-sky-300 font-mono">
                                {formatCountdown(getRemainingSeconds(node.id))}
                              </span>
                            </div>
                            <div className="w-full h-1 bg-zinc-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-sky-500 transition-all duration-1000 rounded-full"
                                style={{ width: `${getProgress(node.id)}%` }}
                              />
                            </div>
                          </div>
                        )}
                        {!complete && !researching && (
                          <p className="text-[10px] text-zinc-600">
                            {locked ? '🔒 Prérequis requis' : `⏱ ${formatDuration(node.duree_recherche_secondes)}`}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Connecteur SVG entre colonnes */}
                {colIdx < columns.length - 1 && (
                  <div className="flex items-center self-stretch mx-1">
                    <svg width="32" height="100%" className="h-full min-h-[80px]" preserveAspectRatio="none">
                      <line x1="0" y1="50%" x2="32" y2="50%" stroke="#3f3f46" strokeWidth="2" strokeDasharray="4 3" />
                      <polygon points="24,45 32,50 24,55" fill="#3f3f46" />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Panneau détail nœud */}
        {selectedNode && (
          <div className="w-80 shrink-0 border-l border-zinc-800/60 overflow-y-auto">
            <NodeDetail
              node={selectedNode}
              playerState={getPlayerNode(selectedNode.id)}
              isComplete={isComplete(selectedNode.id)}
              isResearching={isResearching(selectedNode.id)}
              isUnlocked={isUnlocked(selectedNode)}
              prerequisNodes={selectedNode.prerequis.map((pId) => ({
                id: pId,
                nom: state.nodes.find((n) => n.id === pId)?.nom ?? pId,
                done: isComplete(pId),
              }))}
              remainingSeconds={getRemainingSeconds(selectedNode.id)}
              progress={getProgress(selectedNode.id)}
              anyResearchInProgress={anyResearchInProgress()}
              actionLoading={actionLoading}
              actionError={actionError}
              onStart={() => handleStart(selectedNode.id)}
              onCancel={() => handleCancel(selectedNode.id)}
              onClose={() => setSelectedNode(null)}
              tick={tick}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Panneau détail ────────────────────────────────────────────────────────────

interface NodeDetailProps {
  node: TechNode;
  playerState: PlayerNodeState | null;
  isComplete: boolean;
  isResearching: boolean;
  isUnlocked: boolean;
  prerequisNodes: { id: string; nom: string; done: boolean }[];
  remainingSeconds: number;
  progress: number;
  anyResearchInProgress: boolean;
  actionLoading: boolean;
  actionError: string | null;
  onStart: () => void;
  onCancel: () => void;
  onClose: () => void;
  tick: number; // force re-render countdown
}

const NodeDetail: React.FC<NodeDetailProps> = ({
  node, playerState, isComplete, isResearching, isUnlocked,
  prerequisNodes, remainingSeconds, progress,
  anyResearchInProgress, actionLoading, actionError,
  onStart, onCancel, onClose,
}) => {
  const cfg = CAT_CONFIG[node.categorie];

  return (
    <div className="p-4 flex flex-col gap-4 h-full">
      {/* Header nœud */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{node.icone}</span>
          <div>
            <p className={`text-sm font-bold ${cfg.color}`}>{node.nom}</p>
            <p className={`text-[10px] font-semibold uppercase tracking-wider ${cfg.color} opacity-60`}>
              {cfg.label}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-zinc-600 hover:text-zinc-300 transition-colors mt-0.5"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Description */}
      <p className="text-xs text-zinc-400 leading-relaxed">{node.description}</p>

      {/* Progression si en recherche */}
      {isResearching && (
        <div className={`rounded-xl border ${cfg.border} ${cfg.bg} p-3`}>
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-sky-300">Recherche en cours</span>
            <span className="text-xs font-mono text-sky-200">{formatCountdown(remainingSeconds)}</span>
          </div>
          <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-sky-500 transition-all duration-1000 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-[10px] text-zinc-500 mt-1.5">
            Commencée le {new Date(playerState!.started_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      )}

      {/* Badge complète */}
      {isComplete && (
        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3">
          <svg className="w-5 h-5 text-emerald-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <div>
            <p className="text-xs font-semibold text-emerald-300">Technologie débloquée</p>
            {playerState?.completed_at && (
              <p className="text-[10px] text-zinc-500">
                {new Date(playerState.completed_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Coûts */}
      {!isComplete && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 space-y-1.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Coût</p>
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-400">Argent</span>
            <span className={`text-xs font-semibold ${node.cout_argent > 0 ? 'text-amber-300' : 'text-zinc-500'}`}>
              {node.cout_argent > 0 ? `${node.cout_argent.toLocaleString('fr-FR')} €` : 'Gratuit'}
            </span>
          </div>
          {node.cout_medailles > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-zinc-400">Médailles</span>
              <span className="text-xs font-semibold text-amber-300">{node.cout_medailles} 🏅</span>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-zinc-800 pt-1.5 mt-1.5">
            <span className="text-xs text-zinc-400">Durée</span>
            <span className="text-xs font-semibold text-zinc-200">
              {node.duree_recherche_secondes === 1 ? 'Immédiat' : formatDuration(node.duree_recherche_secondes)}
            </span>
          </div>
        </div>
      )}

      {/* Prérequis */}
      {node.prerequis.length > 0 && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3">
          <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Prérequis</p>
          <div className="space-y-1.5">
            {prerequisNodes.map((p) => (
              <div key={p.id} className="flex items-center gap-2">
                {p.done ? (
                  <svg className="w-3.5 h-3.5 text-emerald-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 text-zinc-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                )}
                <span className={`text-xs ${p.done ? 'text-emerald-400' : 'text-zinc-500'}`}>{p.nom}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Effets */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-2">Effets</p>
        <ul className="space-y-1.5">
          {node.effets.map((effect, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5 shrink-0 text-xs">✦</span>
              <span className="text-xs text-zinc-300 leading-relaxed">{effect}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Erreur */}
      {actionError && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3">
          <p className="text-xs text-red-400">{actionError}</p>
        </div>
      )}

      {/* Actions */}
      <div className="mt-auto space-y-2">
        {isResearching && (
          <button
            type="button"
            onClick={onCancel}
            disabled={actionLoading}
            className="w-full px-4 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-semibold transition-colors disabled:opacity-50 border border-zinc-700"
          >
            {actionLoading ? 'Annulation…' : 'Annuler la recherche (remboursement 50 %)'}
          </button>
        )}
        {!isComplete && !isResearching && isUnlocked && (
          <button
            type="button"
            onClick={onStart}
            disabled={actionLoading || (anyResearchInProgress)}
            className={`w-full px-4 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 ${cfg.bg} ${cfg.color} border ${cfg.border} hover:brightness-125`}
          >
            {actionLoading ? 'Démarrage…' : anyResearchInProgress ? 'Une recherche est déjà en cours' : `Lancer la recherche${node.cout_argent > 0 ? ` · ${node.cout_argent.toLocaleString('fr-FR')} €` : ''}`}
          </button>
        )}
        {!isComplete && !isResearching && !isUnlocked && (
          <div className="w-full px-4 py-2.5 rounded-xl bg-zinc-900 text-zinc-600 text-sm text-center border border-zinc-800">
            🔒 Prérequis non satisfaits
          </div>
        )}
      </div>
    </div>
  );
};

export default TechTreeView;
